<template>
  <div class="grid grid-cols-12 gap-6">
    <!-- BEGIN: Settings -->
    <div class="col-span-12 md:col-span-8">
      <div class="intro-y box grid grid-cols-12 gap-12 p-12">
        <div class="col-span-6">
          <div class="mb-6">
            <label for="team-name" class="form-label text-base"
              >Team Name</label
            >
            <input
              id="team-name"
              v-model.trim="editableTeamName"
              class="form-control mt-2"
              type="text"
              name="team-name"
              autocomplete="off"
            />
          </div>
          <button
            type="button"
            class="btn btn-primary w-40 mr-3"
            @click="updateTeamName()"
          >
            Update Team Name
          </button>
        </div>
        <div class="captain col-span-6">
          <div class="mb-6">
            <CaptainPicker
              :key="captainPickerKey"
              v-model="captain"
              :team-id="teamId"
              :disabled="captainSaving"
              @ready="captainReady = $event"
            />
          </div>
          <button
            type="button"
            class="btn btn-primary captain-update w-40 mr-auto"
            :disabled="!captainReady || captainSaving"
            @click="updateCaptain()"
          >
            {{ captainSaving ? "Updating…" : "Update Captain" }}
          </button>
          <p v-if="captainError" class="text-red mt-3" role="alert">
            {{ captainError }}
          </p>
        </div>
        <div class="col-span-12"><hr /></div>
        <div class="status col-span-6">
          <div
            class="w-full sm:w-auto flex items-center sm:ml-auto mt-3 sm:mt-0"
          >
            <input
              id="is-active"
              v-model.trim="teamActive"
              class="form-check-switch mr-0 ml-0"
              type="checkbox"
              :checked="teamActive"
              @change="updateStatus()"
            />
            <label class="form-check-label font-medium" for="user-active"
              ><span v-show="teamActive">
                Team Is Active
              </span>
              <span v-show="!teamActive">
                Team Is Inactive
              </span></label
            >
          </div>
        </div>
        <div class="status col-span-6">
          <div
            class="w-full sm:w-auto flex items-center sm:ml-auto mt-3 sm:mt-0"
          >
            <input
              id="is-scholarship"
              v-model.trim="teamScholarship"
              class="form-check-switch mr-0 ml-0"
              type="checkbox"
              :checked="teamScholarship"
              @change="updateScholarship()"
            />
            <label class="form-check-label font-medium" for="is-scholarship">
              <span v-show="teamScholarship">
                Team Has Scholarship
              </span>
              <span v-show="!teamScholarship">
                Team Does Not Have Scholarship
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="col-span-12 md:col-span-4">
      <div class="intro-y box grid grid-cols-4">
        <div class="col-span-4 p-12">
          <!--New Button to Resend Team Welcome Message-->
          <button
            v-show="!resendWelcomeMessageCheck"
            type="button"
            class="btn btn-primary mr-auto"
            @click="resendWelcomeMessageCheck = true"
          >
            Resend Team Welcome Message
          </button>
          <div v-show="resendWelcomeMessageCheck" class="mt-3">
            <h3 class="text-primary text-2xl">Double-Check</h3>
            <h3 class="text-base font-bold">Are sure you want to do this?</h3>
            <p>
              This will send the team welcome email to all non-donors on your
              team.
            </p>
            <button
              type="button"
              class="btn btn-primary mt-3 mr-auto"
              @click="triggerTeamWelcomeEmail(teamId)"
            >
              Yes, Resend Welcome Message
            </button>
          </div>
          <!--End new Button to Resend Team Welcome Message-->
          <button
            v-show="!resetTeamCheck"
            type="button"
            class="btn border-red bg-transparent mt-3 w-40 mr-auto"
            @click="resetTeamCheck = true"
          >
            Reset Team
          </button>
          <button
            type="button"
            class="btn btn-red mt-3 mr-auto"
            @click="setTeamMemberEventDates(teamId)"
          >
            Update Event Year for Team Members
          </button>
          <div v-show="resetTeamCheck">
            <h3 class="text-red text-2xl">Danger!</h3>
            <h3 class="text-base font-bold">Are sure you want to do this?</h3>
            <p>
              This will archive the team’s totals as {{ archiveYear }} and reset
              the team to start over for a new Event Year. The team will be
              deactivated and their progress for the year will be reset until
              the team is reactivated.
            </p>
            <button
              type="button"
              class="btn btn-red mt-3 w-40 mr-auto"
              @click="resetThisTeam(teamId)"
            >
              Yes, Reset Team
            </button>
          </div>
          <button
            v-show="!reactivateTeamCheck && !teamActive"
            type="button"
            class="btn border-black block mt-3 w-40 mr-auto"
            @click="reactivateTeamCheck = true"
          >
            Reactivate Team
          </button>
          <div v-show="reactivateTeamCheck" class="mt-3">
            <h3 class="text-green text-2xl">Double-Check</h3>
            <h3 class="text-base font-bold">Are sure you want to do this?</h3>
            <p>
              This will launch the reactivate process for this team. This will
              allow you to activate the team and choose members to reactivate
              and set new goals. This team can also be reactivated by the
              captain of the team if they login using their credentials.
            </p>
            <button
              type="button"
              class="btn btn-red mt-3 w-40 mr-auto"
              @click="reactivateThisTeam(teamId)"
            >
              Yes, Begin Reactivation
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- END: Settings -->
  </div>
</template>

<script>
import { defineComponent, ref, watch, computed } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "@/store";
import CaptainPicker from "@/components/back-end/CaptainPicker.vue";
import { errorMessage } from "@/composables/use-admin-list";
import {
  updateTeamCaptain,
  resetTeam,
  setTeamMembersToNewYear,
  triggerWelcomeEmail
} from "@/api/teams.js";
export default defineComponent({
  components: { CaptainPicker },
  props: {
    teamCaptain: {
      type: String,
      default: function() {
        return "";
      },
      required: true
    },
    teamId: {
      type: String,
      default: function() {
        return "";
      },
      required: true
    },
    isActive: {
      type: Boolean,
      required: true
    },
    isScholarship: {
      type: Boolean,
      required: false,
      default: false
    },
    teamName: {
      type: String,
      default: function() {
        return "";
      },
      required: true
    }
  },
  emits: ["updateTeamData"],
  setup(props, { emit }) {
    const captain = ref(props.teamCaptain);
    const captainReady = ref(false);
    const captainSaving = ref(false);
    const captainError = ref("");
    const captainPickerKey = ref(0);
    const editableTeamName = ref(props.teamName);
    const teamActive = ref(props.isActive);
    const teamScholarship = ref(props.isScholarship);
    const theTeamId = ref(props.teamId);
    const store = useStore();
    const router = useRouter();
    const archiveYear = computed(() =>
      String(Number(store.getters["main/appYear"]) - 1)
    );
    const resetTeamCheck = ref(false);
    const reactivateTeamCheck = ref(false);
    const resendWelcomeMessageCheck = ref(false);

    const sendNotification = async response => {
      let note = {
        show: true,
        title: "Success",
        message: "The team captain has been changed."
      };
      if (response.code) {
        note = {
          show: true,
          error: true,
          title: "Error",
          message: "There was a problem changing the team captain."
        };
      }
      await store.dispatch("notifications/displayNotification", note);
    };

    const updateStatus = () => {
      let updateData = {
        active: teamActive.value
      };
      emit("updateTeamData", updateData);
    };

    const updateTeamName = () => {
      let updateData = {
        name: editableTeamName.value
      };
      emit("updateTeamData", updateData);
    };

    const updateScholarship = () => {
      let updateData = {
        scholarship: teamScholarship.value
      };
      emit("updateTeamData", updateData);
    };

    const updateCaptain = async () => {
      if (!captainReady.value || captainSaving.value) return;
      captainSaving.value = true;
      captainError.value = "";
      try {
        const response = await updateTeamCaptain(
          theTeamId.value,
          captain.value
        );
        if (response.code)
          throw new Error("There was a problem changing the team captain.");
        await sendNotification(response);
        captainReady.value = false;
        captainPickerKey.value++;
        updateStatus();
      } catch (err) {
        captainError.value = errorMessage(err);
      } finally {
        captainSaving.value = false;
      }
    };

    const sendResetNotification = async response => {
      let note = {
        show: true,
        title: "Success",
        message: "The team has been reset."
      };
      if (response.code) {
        note = {
          show: true,
          error: true,
          title: "Error",
          message: "There was a problem resetting the team."
        };
      }
      await store.dispatch("notifications/displayNotification", note);
    };

    const sendTeamYearNotification = async response => {
      let note = {
        show: true,
        title: "Success",
        message: "The team members have been updated."
      };
      if (response.code) {
        note = {
          show: true,
          error: true,
          title: "Error",
          message: "There was a problem updating the team members."
        };
      }
      await store.dispatch("notifications/displayNotification", note);
    };

    const resetThisTeam = async team => {
      const resetResponse = await resetTeam(team);
      await sendResetNotification(resetResponse);
      router.push({ path: "/teams" });
    };

    const reactivateThisTeam = async team => {
      router.push({
        path: `/backend-team-reactivate/${team}/${props.teamCaptain}`
      });
    };

    const setTeamMemberEventDates = async team => {
      const setTeamMemberYearResponse = await setTeamMembersToNewYear(team);
      await sendTeamYearNotification(setTeamMemberYearResponse);
      router.push({ path: "/teams" });
    };

    const sendWelcomeMessageNotification = async response => {
      let note = {
        show: true,
        title: "Success",
        message: "The team welcome message has been sent."
      };
      if (response.code) {
        note = {
          show: true,
          error: true,
          title: "Error",
          message: "There was a problem sending the team welcome message."
        };
      }
      await store.dispatch("notifications/displayNotification", note);
    };

    const triggerTeamWelcomeEmail = async team => {
      const triggerWelcomeMessageResponse = await triggerWelcomeEmail(team);
      await sendWelcomeMessageNotification(triggerWelcomeMessageResponse);
      resendWelcomeMessageCheck.value = false;
    };

    watch(
      () => props.teamName,
      newValue => {
        editableTeamName.value = newValue;
      }
    );

    watch(
      () => props.isScholarship,
      newValue => {
        teamScholarship.value = newValue;
      }
    );

    watch(
      () => props.teamCaptain,
      value => {
        captain.value = value;
      }
    );

    return {
      captain,
      editableTeamName,
      teamActive,
      teamScholarship,
      updateCaptain,
      updateTeamName,
      updateScholarship,
      updateStatus,
      captainReady,
      captainSaving,
      captainError,
      captainPickerKey,
      archiveYear,
      resetTeamCheck,
      resetThisTeam,
      reactivateTeamCheck,
      reactivateThisTeam,
      setTeamMemberEventDates,
      resendWelcomeMessageCheck,
      triggerTeamWelcomeEmail
    };
  }
});
</script>
<style scoped>
.captain-update:disabled {
  background-color: #e5e7eb;
  border-color: #d1d5db;
  color: #6b7280;
  opacity: 1;
  cursor: not-allowed;
  box-shadow: none;
}
</style>
